import { useEffect, useRef, useState, useCallback } from "react";
import _ from "lodash";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useUserContext } from "../contexts";
import { useNavigate } from "react-router-dom";

const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];

const AutoLogout = ({
  timeout = 30 * 60 * 1000, //logout after 30 mins of inactivity
  alertTimeout = 25 * 60 * 1000, //alert after 25 mins of inactivity
}) => {
  const timeoutRef = useRef();
  const alertRef = useRef();
  const logoutRef = useRef();
  const intervalRef = useRef();
  const isMountedRef = useRef(true);
  const { user, logout } = useUserContext();
  const navigate = useNavigate();

  const [show, setShow] = useState(false);
  const [showExpiredWarning, setShowExpiredWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timeout - alertTimeout);
  const [stayLoggedIn, setStayLoggedIn] = useState(false);

  const resetTimer = useCallback(() => {
    if (!user?.isAuthenticated || !user?.cookieExpiry) return;

    const localExpiryDate = new Date(user.cookieExpiry);
    if (isNaN(localExpiryDate.getTime())) return;

    clearTimeout(timeoutRef.current);
    clearTimeout(alertRef.current);
    clearInterval(intervalRef.current);

    setTimeLeft(timeout - alertTimeout - 1000);

    const startTime = Date.now();
    const cookieExpiryTime = localExpiryDate.getTime();
    const alertCountdown = timeout - alertTimeout;

    // If cookie will expire before alert, skip alert
    if (cookieExpiryTime <= Date.now() + alertCountdown) {
      timeoutRef.current = setTimeout(
        handleCookieExpiry,
        cookieExpiryTime - Date.now()
      );
      return;
    }

    alertRef.current = setTimeout(() => triggerAlert(startTime), alertTimeout);
    timeoutRef.current = setTimeout(() => {
      setTimeLeft(0);
      handleLogout();
    }, timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeout, alertTimeout, user]);

  const debouncedResetTimer = useRef(_.debounce(resetTimer, 300)).current;

  useEffect(() => {
    isMountedRef.current = true;

    if (user?.isAuthenticated) {
      debouncedResetTimer(); //start timer on login
      events.forEach(
        (event) => window.addEventListener(event, debouncedResetTimer) //reset timer on activity
      );
    }
    return () => {
      isMountedRef.current = false;
      events.forEach((event) =>
        window.removeEventListener(event, debouncedResetTimer)
      );
      clearTimeout(timeoutRef.current);
      clearTimeout(alertRef.current);
      clearInterval(intervalRef.current);
      if (debouncedResetTimer.cancel) debouncedResetTimer.cancel(); //cancel any pending debounced calls
    };
  }, [debouncedResetTimer, user?.isAuthenticated]);

  const handleShow = () => {
    setStayLoggedIn(false);
    setShow(true);
    events.forEach((event) =>
      window.removeEventListener(event, debouncedResetTimer)
    );
  };

  const handleClose = () => {
    setShow(false);
    events.forEach((event) =>
      window.addEventListener(event, debouncedResetTimer)
    );
  };

  const handleCookieExpiry = () => {
    if (!isMountedRef.current) return;
    setShowExpiredWarning(true);
    logoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      setShowExpiredWarning(false);
      handleLogout();
    }, 3500);
  };

  const triggerAlert = (startTime) => {
    intervalRef.current = setInterval(() => {
      if (!isMountedRef.current) return;
      const timeLeft = timeout - (Date.now() - startTime);
      setTimeLeft(timeLeft);
      if (timeLeft <= 0) {
        clearInterval(intervalRef.current);
      }
    }, 1000);
    handleShow();
  };

  const handleLogout = async () => {
    events.forEach((event) =>
      window.removeEventListener(event, debouncedResetTimer)
    );
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop='static'
        keyboard={false}
        centered
      >
        <Modal.Header
          style={{ borderBottomColor: "#F08021", borderBottomWidth: "4px" }}
          closeButton
        >
          <strong className='me-auto'>Still with us?</strong>
        </Modal.Header>
        <Modal.Body
          className='d-flex flex-column text-center justify-content-center align-items-center'
          style={{ minHeight: "200px" }}
        >
          {timeLeft > 0 ? (
            stayLoggedIn ? (
              <></>
            ) : (
              <>
                <div className='fs-4 fw-bold'>
                  <i className='bi bi-stopwatch' /> Your session will expire in:{" "}
                </div>
                <div className='fs-1 fw-bold'>
                  {Math.floor(timeLeft / 60 / 1000)} minutes{" "}
                  {Math.floor(
                    (timeLeft / 60 / 1000 - Math.floor(timeLeft / 60 / 1000)) *
                      60
                  )}{" "}
                  seconds
                </div>
                <br />
                <p>
                  Your privacy is important to us, and you've been inactive for
                  a while.
                  <br />
                  Please choose "Stay Logged In" to continue your session.
                </p>
              </>
            )
          ) : (
            <div className='fs-1 fw-bold'>Logging out...</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outline-secondary'
            onClick={() => {
              handleClose();
              handleLogout();
            }}
          >
            Logout
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setStayLoggedIn(true);
              handleClose();
              resetTimer();
            }}
          >
            Stay Logged In
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showExpiredWarning}
        onHide={() => {
          setShowExpiredWarning(false);
          clearTimeout(logoutRef.current);
        }}
        backdrop='static'
        keyboard={false}
        centered
      >
        <Modal.Header
          style={{ borderBottomColor: "#F08021", borderBottomWidth: "4px" }}
        >
          <strong className='me-auto'>Session Expiry</strong>
        </Modal.Header>
        <Modal.Body
          className='d-flex flex-column text-center justify-content-center align-items-center'
          style={{ minHeight: "200px" }}
        >
          <div className='fs-4 fw-bold'>
            <i className='bi bi-stopwatch' /> Your session has expired.
          </div>
          <div className='fs-1 fw-bold'>Logging out...</div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AutoLogout;
